import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Fingerprint,
  FileCheck2,
  Landmark,
  Zap,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import verificationImg from "../Accets/verification.png";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const features = [
  { icon: ShieldCheck, title: "KYC Verification", desc: "Instant user onboarding checks." },
  { icon: FileCheck2, title: "PAN Validation", desc: "Verify PAN details in real-time." },
  { icon: Landmark, title: "Bank Verification", desc: "Check account authenticity." },
  { icon: Zap, title: "Fast API", desc: "Lightning fast response times." },
];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto justify-center" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// ---------------------------------------------------------------------
// NEW hero background — "identity scan" motif. A scanning frame with
// corner brackets sweeps top-to-bottom over a faint grid of ID-card
// outlines, like documents being verified one by one, with a soft
// ambient glow and small checkmark pulses appearing as it passes.
// ---------------------------------------------------------------------
const idCards = [
  { x: 8, y: 14 }, { x: 30, y: 10 }, { x: 54, y: 18 }, { x: 78, y: 12 },
  { x: 16, y: 40 }, { x: 42, y: 46 }, { x: 66, y: 38 }, { x: 88, y: 44 },
  { x: 10, y: 68 }, { x: 34, y: 74 }, { x: 58, y: 66 }, { x: 82, y: 72 },
];

function ScanField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <DotGrid light />
      <span className="scan-glow" />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {idCards.map((c, i) => (
          <rect
            key={i}
            x={c.x} y={c.y} width="9" height="6" rx="1"
            fill="none"
            stroke={i % 3 === 0 ? "rgba(217,164,65,0.3)" : "rgba(255,255,255,0.16)"}
            strokeWidth="0.25"
          />
        ))}
      </svg>

      <span className="scan-frame">
        <span className="scan-corner scan-corner-tl" />
        <span className="scan-corner scan-corner-tr" />
        <span className="scan-corner scan-corner-bl" />
        <span className="scan-corner scan-corner-br" />
        <span className="scan-line" />
      </span>
    </div>
  );
}

export default function VerificationSuite() {
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 40);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .scan-glow {
          position: absolute; top: 45%; left: 30%; width: 30rem; height: 30rem;
          margin: -15rem 0 0 -15rem; border-radius: 9999px;
          background: radial-gradient(circle, ${theme.accent}28, transparent 68%);
          filter: blur(70px);
          animation: scanGlowBreathe 7s ease-in-out infinite;
        }
        @keyframes scanGlowBreathe {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.12); }
        }

        .scan-frame {
          position: absolute; left: 6%; right: 46%; top: 0; height: 40%;
          animation: scanFrameMove 6s ease-in-out infinite;
        }
        @keyframes scanFrameMove {
          0%, 100% { top: 6%; }
          50% { top: 54%; }
        }
        .scan-corner {
          position: absolute; width: 18px; height: 18px;
          border-color: ${theme.accent};
        }
        .scan-corner-tl { top: 0; left: 0; border-top: 2px solid; border-left: 2px solid; }
        .scan-corner-tr { top: 0; right: 0; border-top: 2px solid; border-right: 2px solid; }
        .scan-corner-bl { bottom: 0; left: 0; border-bottom: 2px solid; border-left: 2px solid; }
        .scan-corner-br { bottom: 0; right: 0; border-bottom: 2px solid; border-right: 2px solid; }
        .scan-line {
          position: absolute; left: 0; right: 0; top: 50%; height: 1px;
          background: linear-gradient(90deg, transparent, ${theme.accent}, transparent);
          box-shadow: 0 0 10px 2px ${theme.accent}88;
        }

        @media (prefers-reduced-motion: reduce) {
          .scan-glow, .scan-frame { animation: none !important; }
        }

        .card-glow-light {
          pointer-events: none; position: absolute; top: -3rem; right: -3rem; width: 9rem; height: 9rem;
          border-radius: 9999px; filter: blur(30px); background: ${theme.accent}; opacity: 0.08; transition: opacity 0.3s ease;
        }
        .group:hover .card-glow-light { opacity: 0.18; }
      `}</style>

      {/* ================= HERO (dark, scan field) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
          <ScanField />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow light>Verification Suite</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-white">
              <SplitText text="Secure identity" />
              <br />
              <span style={{ color: theme.accent }}>
                <SplitText text="verification at scale" delay={0.25} />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="text-white/60 mt-8 text-lg leading-8 max-w-xl"
            >
              Perform instant KYC, PAN, Aadhaar, GST and bank verification with
              enterprise-grade accuracy and speed.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 px-8 py-4 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Start Verification
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-[32px] blur-[100px] opacity-20" style={{ backgroundColor: theme.accent }} />
            <img src={verificationImg} alt="Verification Suite" className="relative rounded-[32px] border border-white/10 shadow-2xl" />
          </motion.div>
        </div>
      </section>

      {/* ================= FEATURES (light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-2xl mx-auto mb-16">
            <Eyebrow center>Capabilities</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-bold" style={{ color: theme.ink }}>
              Why choose <span style={{ color: theme.accent }}>Verification Suite?</span>
            </h2>
            <p className="mt-4" style={{ color: theme.inkMuted }}>
              Fast, compliant and scalable identity verification system.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((item) => (
              <motion.div
                key={item.title}
                variants={staggerItem}
                whileHover={{ y: -8 }}
                className="group relative rounded-3xl p-8 overflow-hidden"
                style={{ backgroundColor: theme.surface, border: `1px solid ${theme.border}` }}
              >
                <span className="card-glow-light" />
                <div className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}>
                  <item.icon size={26} />
                </div>
                <h3 className="relative text-2xl font-semibold" style={{ color: theme.ink }}>{item.title}</h3>
                <p className="relative mt-4" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-bold leading-tight text-white">
            <SplitText text="Ready to verify" />
            <span className="block">
              <SplitText text="with confidence?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Plug into secure, compliant identity verification in minutes.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Contact Sales
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}